import React, { useState, useRef } from 'react';
import { Camera, Upload, X, AlertCircle, CheckCircle, Loader2, AlertTriangle } from 'lucide-react';
import { scanPathologyReport } from '../utils/api';
import { toast } from 'sonner';

const PhotoScanBloodResults = ({ onScanComplete, onCancel }) => {
  const [scanning, setScanning] = useState(false);
  const [error, setError] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const fileInputRef = useRef(null);

  const handleFileSelect = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    const validTypes = ['image/jpeg', 'image/png', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      setError('Please select a JPEG, PNG or WEBP image.');
      return;
    }

    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      setError('File is too large. Please select a file under 10MB.');
      return;
    }

    setError(null);

    // Show preview
    if (file.type.startsWith('image/')) {
      const url = URL.createObjectURL(file);
      setPreviewUrl(url);
    }

    // Convert to base64
    const reader = new FileReader();
    reader.onload = async () => {
      const base64 = reader.result;
      await processImage(base64, file.type);
    };
    reader.onerror = () => {
      setError('Failed to read the file. Please try again.');
    };
    reader.readAsDataURL(file);
  };

  const processImage = async (base64Data, mimeType) => {
    setScanning(true);
    setError(null);

    try {
      const result = await scanPathologyReport(base64Data, mimeType);
      
      if (result.success) {
        // Transform the result into a format the parent can use
        const extractedData = {
          date: result.collection_date || new Date().toISOString().split('T')[0],
          wbc: result.wbc?.value || '',
          lymphocytes: result.lymphocytes?.value || '',
          platelets: result.platelets?.value || '',
          haemoglobin: result.haemoglobin?.value || '',
          neutrophils: result.neutrophils?.value || '',
          // Unit detected on the report for haemoglobin (g/dL or g/L) - backend converts once on save
          haemoglobin_unit: result.haemoglobin?.unit || null,
          // Include confidence info for review UI
          confidence: {
            wbc: result.wbc,
            lymphocytes: result.lymphocytes,
            platelets: result.platelets,
            haemoglobin: result.haemoglobin,
            neutrophils: result.neutrophils,
          }
        };
        
        onScanComplete(extractedData);
        toast.success('Report scanned successfully! Please verify the values.');
      } else {
        setError(result.error_message || 'Could not read the report. Please try manual entry.');
      }
    } catch (err) {
      console.error('Scan error:', err);
      setError('Failed to process the report. Please try manual entry.');
    } finally {
      setScanning(false);
    }
  };

  const handleDrop = (event) => {
    event.preventDefault();
    event.stopPropagation();
    
    const file = event.dataTransfer.files?.[0];
    if (file) {
      // Create a synthetic event with the file
      const syntheticEvent = {
        target: { files: [file] }
      };
      handleFileSelect(syntheticEvent);
    }
  };

  const handleDragOver = (event) => {
    event.preventDefault();
    event.stopPropagation();
  };

  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-6" data-testid="photo-scan-component">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-blue-50 rounded-lg">
            <Camera className="h-6 w-6 text-blue-600" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-slate-900">Scan Pathology Report</h2>
            <p className="text-sm text-slate-600">Upload a photo of your blood test results</p>
          </div>
        </div>
        {onCancel && (
          <button
            onClick={onCancel}
            className="p-2 text-slate-400 hover:text-slate-600 transition-colors"
            aria-label="Cancel"
          >
            <X className="h-5 w-5" />
          </button>
        )}
      </div>

      {/* Privacy Notice */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-6">
        <p className="text-xs text-blue-800 flex items-start gap-2">
          <AlertCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />
          <span>Your report is processed securely and isn't shared with anyone. The image is discarded after processing.</span>
        </p>
      </div>

      {/* Scanning State */}
      {scanning ? (
        <div className="text-center py-12">
          <Loader2 className="h-12 w-12 text-blue-600 animate-spin mx-auto mb-4" />
          <p className="text-lg font-medium text-slate-900 mb-2">Reading your report...</p>
          <p className="text-sm text-slate-600">This may take a few seconds</p>
        </div>
      ) : (
        <>
          {/* Preview */}
          {previewUrl && !error && (
            <div className="mb-6">
              <img 
                src={previewUrl} 
                alt="Report preview" 
                className="max-h-48 mx-auto rounded-lg border border-slate-200"
              />
            </div>
          )}

          {/* Error State */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
              <div className="flex items-start gap-3">
                <AlertTriangle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-red-800 font-medium">Couldn't read the report</p>
                  <p className="text-sm text-red-700 mt-1">{error}</p>
                </div>
              </div>
            </div>
          )}

          {/* Upload Area */}
          <div
            onClick={() => fileInputRef.current?.click()}
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            className="border-2 border-dashed border-slate-300 rounded-xl p-8 text-center cursor-pointer hover:border-blue-400 hover:bg-blue-50/50 transition-all"
          >
            <div className="flex flex-col items-center gap-3">
              <div className="p-4 bg-slate-100 rounded-full">
                <Upload className="h-8 w-8 text-slate-600" />
              </div>
              <div>
                <p className="font-medium text-slate-900 mb-1">
                  Click to upload or drag and drop
                </p>
                <p className="text-sm text-slate-600">
                  JPEG, PNG or WEBP (max 10MB)
                </p>
              </div>
            </div>
          </div>

          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            onChange={handleFileSelect}
            className="hidden"
            data-testid="file-input"
          />

          {/* Supported Labs */}
          <div className="mt-6 text-center">
            <p className="text-xs text-slate-500 mb-2">Recognizes reports from:</p>
            <p className="text-xs text-slate-400">
              Sullivan Nicolaides • QML • Douglass Hanly Moir • 4Cyte • Melbourne Pathology • Hospital reports
            </p>
          </div>
        </>
      )}
    </div>
  );
};

export default PhotoScanBloodResults;
